import { useCallback, useEffect, useRef, useState } from 'react';
import { useWebSocket } from './useWebSocket';
import { useAuthStore } from '@stores/authStore';

/**
 * #18947 / BUG-040 — live collision detection for a single ticket.
 *
 * The backend already broadcasts presence over the ticket room (see the BFF
 * websocket gateway `ticket:presence` / `ticket:typing` handlers); the web app
 * simply never joined the room or announced itself, so two agents on the same
 * ticket never saw each other. This hook wires that up:
 *
 *   - joins the `ticket:<id>` room while mounted,
 *   - announces "viewing" on open and re-announces every ANNOUNCE_MS so peers'
 *     entries stay fresh (this doubles as a mutual heartbeat),
 *   - exposes notifyTyping() for the reply composer,
 *   - tracks OTHER users from their `ticket:user_activity` / `ticket:typing`
 *     broadcasts, pruning anyone not heard from within STALE_MS,
 *   - announces "leave" on unmount.
 *
 * Presence is best-effort and self-healing: a dropped frame only makes the
 * banner briefly stale, never wrong for long.
 */
export interface TicketPresenceUser {
  userId: string;
  email: string;
  isTyping: boolean;
  lastSeen: number;
}

// Re-announce presence this often. Comfortably under STALE_MS so a peer who is
// still on the ticket is never pruned between announcements.
const ANNOUNCE_MS = 8000;
// Drop a peer we haven't heard from in this long (≈2 missed announcements).
const STALE_MS = 20000;
const PRUNE_MS = 4000;

export function useTicketCollision(ticketId?: string) {
  const { subscribeToTicket, on, off, emit } = useWebSocket();
  const selfId = useAuthStore((s) => s.user?.id);
  const [others, setOthers] = useState<Record<string, TicketPresenceUser>>({});

  const upsert = useCallback(
    (userId: string | undefined, email: string | undefined, isTyping: boolean) => {
      if (!userId || userId === selfId) return;
      setOthers((prev) => ({
        ...prev,
        [userId]: { userId, email: email || 'A teammate', isTyping, lastSeen: Date.now() },
      }));
    },
    [selfId],
  );

  useEffect(() => {
    if (!ticketId) return;

    const leaveRoom = subscribeToTicket(ticketId);

    const announce = () => emit('ticket:presence', { ticketId, activityType: 'viewing' });
    announce();
    const announceTimer = setInterval(announce, ANNOUNCE_MS);

    const onActivity = (d: { userId: string; email: string; activityType?: string }) =>
      upsert(d.userId, d.email, d.activityType === 'typing');
    const onTyping = (d: { userId: string; email: string; isTyping: boolean }) =>
      upsert(d.userId, d.email, !!d.isTyping);
    const onCollision = (d: { userId: string; userName: string; activityType?: string }) =>
      upsert(d.userId, d.userName, d.activityType === 'typing');
    const onLeft = (d: { userId: string }) =>
      setOthers((prev) => {
        if (!prev[d.userId]) return prev;
        const next = { ...prev };
        delete next[d.userId];
        return next;
      });

    on('ticket:user_activity', onActivity);
    on('ticket:typing', onTyping);
    on('ticket:collision', onCollision);
    on('ticket:user_left', onLeft);

    const pruneTimer = setInterval(() => {
      setOthers((prev) => {
        const now = Date.now();
        let changed = false;
        const next = { ...prev };
        for (const key of Object.keys(next)) {
          if (now - next[key].lastSeen > STALE_MS) {
            delete next[key];
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }, PRUNE_MS);

    return () => {
      clearInterval(announceTimer);
      clearInterval(pruneTimer);
      off('ticket:user_activity', onActivity);
      off('ticket:typing', onTyping);
      off('ticket:collision', onCollision);
      off('ticket:user_left', onLeft);
      emit('ticket:leave', { ticketId });
      leaveRoom();
      setOthers({});
    };
  }, [ticketId, subscribeToTicket, on, off, emit, upsert]);

  const notifyTyping = useCallback(
    (isTyping: boolean) => {
      if (ticketId) emit('ticket:typing', { ticketId, isTyping });
    },
    [ticketId, emit],
  );

  return { activeUsers: Object.values(others), notifyTyping };
}
